/**
 * Стек выполнения — состояние экрана «План» (DEV-1656).
 *
 * Правки применяются сразу, до ответа сервера: план переставляют руками, и
 * строка, доезжающая до места через полсекунды, читается как «не сработало».
 * Расплата — честный откат: снимок порядка снимается до переноса и
 * возвращается, если сервер отказал.
 *
 * Ответ сервера всегда заменяет список целиком, а не правит его по месту:
 * позиции считает он, и второй порядок на клиенте расходился бы с первым
 * молча.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  getStack,
  getStats,
  pushToStack,
  removeFromStack,
  reorderStack,
  type PlanStats,
  type PushPlace,
  type StackItem,
} from './api'

export const usePlanStore = defineStore('plan', () => {
  const items = ref<StackItem[]>([])
  const stats = ref<PlanStats | null>(null)
  const isLoading = ref(false)
  const isLoaded = ref(false)
  const error = ref<unknown>(null)

  /** Пока строку тащат, стек не перечитывается из-под руки */
  const isDragging = ref(false)

  /** Верх стека — то, чем человек занят сейчас; на нём держится вся метафора */
  const current = computed(() => items.value[0] ?? null)

  const load = async () => {
    isLoading.value = true
    error.value = null

    try {
      items.value = await getStack()
      isLoaded.value = true
    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Цифры грузятся отдельно от стека: они считаются агрегатами по задачам и
   * приходят заметно позже списка, а список без них уже рабочий.
   */
  const loadStats = async () => {
    try {
      stats.value = await getStats()
    } catch {
      // Виджеты — справка, а не работа: их отсутствие не повод показывать
      // ошибку на весь экран. Пустое место скажет то же самое и не помешает.
      stats.value = null
    }
  }

  /**
   * Порядок на момент захвата — то, к чему возвращаемся при отказе. Снимок
   * снимается до переноса, а не перед отправкой: к моменту отправки строку уже
   * переставил vuedraggable, и «откат» вернул бы ровно то, что нужно отменить.
   */
  let dragSnapshot: StackItem[] | null = null

  const beginDrag = () => {
    isDragging.value = true
    dragSnapshot = [...items.value]
  }

  const endDrag = () => {
    isDragging.value = false
  }

  /** Строку в списке уже переставили — отсюда уходит только новый порядок */
  const applyOrder = async () => {
    const saved = dragSnapshot

    try {
      items.value = await reorderStack(items.value.map((item) => item.key))
    } catch (failure) {
      if (saved) items.value = saved
      error.value = failure
    }
  }

  /**
   * Переставить строку на место `index` — тот же порядок, но без мыши: им
   * пользуются кнопки вверх/вниз на телефоне (DEV-1660).
   */
  const moveTo = async (key: string, index: number) => {
    const from = items.value.findIndex((item) => item.key === key)

    if (from === -1 || index < 0 || index >= items.value.length || index === from) return

    const saved = [...items.value]
    const next = [...items.value]
    const [moved] = next.splice(from, 1)

    if (!moved) return

    next.splice(index, 0, moved)
    items.value = next

    try {
      items.value = await reorderStack(next.map((item) => item.key))
    } catch (failure) {
      items.value = saved
      error.value = failure
    }
  }

  const push = async (issueKey: string, place: PushPlace = {}) => {
    try {
      items.value = await pushToStack(issueKey, place)
      void loadStats()
    } catch (failure) {
      error.value = failure
    }
  }

  const remove = async (issueKey: string) => {
    const saved = [...items.value]

    items.value = items.value.filter((item) => item.key !== issueKey)

    try {
      items.value = await removeFromStack(issueKey)
      void loadStats()
    } catch (failure) {
      items.value = saved
      error.value = failure
    }
  }

  /** Ключи стека — по ним панель кандидатов гасит то, что уже в плане */
  const keys = computed(() => new Set(items.value.map((item) => item.key)))

  return {
    items,
    stats,
    isLoading,
    isLoaded,
    isDragging,
    error,
    current,
    keys,
    load,
    loadStats,
    beginDrag,
    endDrag,
    applyOrder,
    moveTo,
    push,
    remove,
  }
})
